import axios from "axios";
import { AuthResult, toAuthError } from "./auth.errors";

// Deliberately its own axios instance, like tokenRefresh.ts's refreshClient, and
// deliberately NOT wired to attachAuthRefresh().
//
// The global interceptors in src/index.tsx are hostile to a login screen:
//   * the response interceptor turns any 401/403 into refreshAccessToken() and,
//     when that returns null — which it always does here, since there is no
//     refresh token yet — redirects to /login. It *returns* that redirect, so
//     the promise resolves undefined instead of rejecting and .catch() never
//     runs. A wrong password would silently reload the page.
//   * the request interceptor force-logs-out whenever lastLogoutDate is missing
//     or stale, which would abort the login request itself.
// Owning the instance sidesteps both, and removes the need for the
// `headers: { Authorization: "" }` override the OTP version had to pass.
const baseURL =
  process.env.REACT_APP_API_URL || "https://api-dev2.materialdepot.in/apiV1";
const authClient = axios.create({ baseURL });

// The entire backend coupling, in one place. Relative to the client's baseURL,
// matching tokenRefresh.ts. Kept out of routes.ts on purpose: that file holds 2
// of the ~200 endpoints in this app, so adding these would imply an authority it
// does not have.
const LOGIN_PATH = "/password/login/";
const PASSWORD_OTP_PATH = "/password/otp/";
const SET_PASSWORD_PATH = "/password/set/";

/** Phone + password sign-in. Resolves — never rejects. */
export async function loginWithPassword(
  phone: string,
  password: string
): Promise<AuthResult> {
  try {
    const res = await authClient.post(LOGIN_PATH, {
      contact: phone,
      password,
    });
    if (!res?.data?.token) {
      // 200 without a token means the contract moved under us; treat it as a
      // failure rather than storing `undefined` and bouncing the user forever.
      return toAuthError({ response: res }, "Login failed. Please try again.");
    }
    return {
      ok: true,
      token: res.data.token,
      refresh: res.data.refresh,
      user: res.data.user,
    };
  } catch (e) {
    return toAuthError(e, "Login failed. Please try again.");
  }
}

/**
 * Send the one-time code that authorises setting or resetting a password.
 *
 * POST, not GET: the OTP version put the number in a query string, where it
 * outlives the request in access logs, browser history and Referer headers.
 *
 * The backend answers the same 200 whether or not the number belongs to staff,
 * so a success here does NOT confirm the account exists.
 */
export async function requestPasswordOtp(phone: string): Promise<AuthResult> {
  try {
    const res = await authClient.post(PASSWORD_OTP_PATH, { contact: phone });
    return { ok: true, message: res?.data?.detail };
  } catch (e) {
    return toAuthError(e, "Could not send the code. Please try again.");
  }
}

/**
 * Prove the code and set the password in one request, so the 120-second OTP
 * cannot expire between two screens. Returns tokens — the backend signs the user
 * straight in, since they have just proven the number and chosen the password.
 */
export async function setPasswordWithOtp(
  phone: string,
  otp: string,
  password: string
): Promise<AuthResult> {
  try {
    const res = await authClient.post(SET_PASSWORD_PATH, {
      contact: phone,
      otp,
      password,
    });
    return {
      ok: true,
      token: res?.data?.token,
      refresh: res?.data?.refresh,
      user: res?.data?.user,
    };
  } catch (e) {
    return toAuthError(e, "Could not set your password. Please try again.");
  }
}
